import { PlusIcon, XIcon } from "lucide-react";
import { useMemo, useState } from "react";

import {
  AlertDialog,
  AlertDialogClose,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogPopup,
  AlertDialogTitle,
} from "~/components/ui/alert-dialog";
import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import { Menu, MenuItem, MenuPopup, MenuTrigger } from "~/components/ui/menu";
import { cn } from "~/lib/utils";

const MEMBER_ROLE = "member";

function formatRoleLabel(role: string): string {
  return role.replaceAll("_", " ");
}

function ensureMember(roles: readonly string[]): string[] {
  const next = [...new Set(roles)];
  if (!next.includes(MEMBER_ROLE)) {
    next.unshift(MEMBER_ROLE);
  }
  return next;
}

type PendingChange =
  | {
      description: string;
      kind: "grant";
      roleKey: string;
      title: string;
    }
  | {
      description: string;
      kind: "revoke";
      roleKey: string;
      title: string;
    };

export function AdminUserRolesCombobox(props: {
  busy: boolean;
  onGrant: (roleKey: string) => Promise<void>;
  onRevoke: (roleKey: string) => Promise<void>;
  roleKeys: readonly string[];
  roles: readonly string[];
}) {
  const [pending, setPending] = useState<PendingChange | null>(null);
  const [confirming, setConfirming] = useState(false);

  const selectedRoles = useMemo(() => ensureMember(props.roles), [props.roles]);
  const availableToAdd = useMemo(() => {
    const selected = new Set(selectedRoles);
    const keys = new Set([...props.roleKeys, MEMBER_ROLE]);
    return [...keys]
      .filter((role) => !selected.has(role))
      .sort((left, right) => left.localeCompare(right));
  }, [props.roleKeys, selectedRoles]);

  const requestGrant = (roleKey: string) => {
    setPending({
      kind: "grant",
      roleKey,
      title: "Grant role?",
      description: `This will grant “${formatRoleLabel(roleKey)}” for this user. Role changes apply on their next request (usually within 30 seconds).`,
    });
  };

  const requestRevoke = (roleKey: string) => {
    if (roleKey === MEMBER_ROLE) {
      return;
    }
    setPending({
      kind: "revoke",
      roleKey,
      title: "Revoke role?",
      description: `This will revoke “${formatRoleLabel(roleKey)}” for this user. Role changes apply on their next request (usually within 30 seconds).`,
    });
  };

  const confirmPending = async () => {
    if (!pending) {
      return;
    }
    setConfirming(true);
    try {
      if (pending.kind === "grant") {
        await props.onGrant(pending.roleKey);
      } else {
        await props.onRevoke(pending.roleKey);
      }
      setPending(null);
    } finally {
      setConfirming(false);
    }
  };

  return (
    <>
      <div
        className="flex flex-wrap items-center gap-1.5"
        onClick={(event) => {
          event.stopPropagation();
        }}
        onKeyDown={(event) => {
          event.stopPropagation();
        }}
      >
        {selectedRoles.map((role) => {
          const isMember = role === MEMBER_ROLE;
          return (
            <Badge
              key={role}
              variant="secondary"
              className={cn("gap-0.5 pe-1", isMember && "pe-1.5")}
            >
              {formatRoleLabel(role)}
              {!isMember ? (
                <button
                  type="button"
                  aria-label={`Revoke ${formatRoleLabel(role)}`}
                  disabled={props.busy || confirming}
                  className="inline-flex size-4 items-center justify-center rounded-sm text-muted-foreground transition-colors hover:text-foreground disabled:opacity-50"
                  onClick={() => requestRevoke(role)}
                >
                  <XIcon className="size-3" />
                </button>
              ) : null}
            </Badge>
          );
        })}

        <Menu>
          <MenuTrigger
            render={
              <Button
                size="icon-xs"
                variant="outline"
                aria-label="Add role"
                disabled={props.busy || confirming || availableToAdd.length === 0}
              />
            }
          >
            <PlusIcon className="size-3.5" />
          </MenuTrigger>
          <MenuPopup align="start" side="bottom" className="min-w-44">
            {availableToAdd.map((role) => (
              <MenuItem key={role} onClick={() => requestGrant(role)}>
                {formatRoleLabel(role)}
              </MenuItem>
            ))}
          </MenuPopup>
        </Menu>
      </div>

      <AlertDialog
        open={pending !== null}
        onOpenChange={(open) => {
          if (!open && !confirming) {
            setPending(null);
          }
        }}
      >
        <AlertDialogPopup>
          <AlertDialogHeader>
            <AlertDialogTitle>{pending?.title ?? "Change roles?"}</AlertDialogTitle>
            <AlertDialogDescription>{pending?.description}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogClose render={<Button variant="outline" disabled={confirming} />}>
              Cancel
            </AlertDialogClose>
            <Button disabled={confirming} onClick={() => void confirmPending()}>
              {confirming ? "Saving…" : "Confirm"}
            </Button>
          </AlertDialogFooter>
        </AlertDialogPopup>
      </AlertDialog>
    </>
  );
}
